import { retryBusy } from "../nest/appDataRetry"
import { NestClientError, requestNestMethod, type NestMethodParams, type NestMethodResult } from "../nest/client"
import { parsePersonalSettingsRecord, SETTINGS_KEY, SETTINGS_NAMESPACE, type PersonalSettingsRecord } from "./records"
import {
    createPersonalSettingsStoreFromValue,
    normalizePersonalSettings,
    replacePersonalSettings,
    type PersonalSettings,
    type PersonalSettingsStore,
} from "./personalSettingsStore"

type PersonalSettingsMethod = "appData.get" | "appData.set"
export type PersonalSettingsRequest = <M extends PersonalSettingsMethod>(method: M, params: NestMethodParams<M>) => Promise<NestMethodResult<M>>

export interface PersonalSettingsAppDataConnection {
    store: PersonalSettingsStore
    sync: () => Promise<void>
    disconnect: () => void
}

interface PersonalSettingsAppDataOptions {
    request?: PersonalSettingsRequest
    debounceMs?: number
    now?: () => string
}

const DEFAULT_WRITE_DEBOUNCE_MS = 25

const defaultRequest: PersonalSettingsRequest = (method, params) => requestNestMethod(method, params)

function settingsRecord(settings: PersonalSettings, updatedAt: string): PersonalSettingsRecord {
    return {
        v: 1,
        settings,
        updatedAt,
    }
}

function hasSettingsPatch(patch: Partial<PersonalSettings>): boolean {
    return Object.keys(patch).length > 0
}

export async function connectPersonalSettingsAppDataStore(options: PersonalSettingsAppDataOptions = {}): Promise<PersonalSettingsAppDataConnection> {
    const request = options.request ?? defaultRequest
    const debounceMs = options.debounceMs ?? DEFAULT_WRITE_DEBOUNCE_MS
    const now = options.now ?? (() => new Date().toISOString())
    let version: number | undefined
    let disposed = false
    let writeTimer: ReturnType<typeof globalThis.setTimeout> | null = null
    let writeQueue: Promise<void> = Promise.resolve()
    let pendingPatch: Partial<PersonalSettings> = {}

    const result = await retryBusy(() =>
        request("appData.get", {
            namespace: SETTINGS_NAMESPACE,
            key: SETTINGS_KEY,
        })
    )
    version = result.version
    const parsed = result.found ? parsePersonalSettingsRecord(result.value) : null
    const store = createPersonalSettingsStoreFromValue(parsed?.settings, (_settings, patch) => {
        if (!hasSettingsPatch(patch)) return
        pendingPatch = { ...pendingPatch, ...patch }
        if (disposed || writeTimer) return
        writeTimer = globalThis.setTimeout(() => {
            writeTimer = null
            void sync()
        }, debounceMs)
    })

    async function writeSettings(settings: PersonalSettings, patch: Partial<PersonalSettings>, retryConflict: boolean): Promise<void> {
        const expectedVersion = version
        try {
            const setResult = await retryBusy(() =>
                request("appData.set", {
                    namespace: SETTINGS_NAMESPACE,
                    key: SETTINGS_KEY,
                    value: settingsRecord(settings, now()),
                    ...(expectedVersion !== undefined ? { expectedVersion } : {}),
                })
            )
            version = setResult.version
        } catch (error) {
            if (!retryConflict || !(error instanceof NestClientError) || error.code !== "version_conflict") throw error
            const current = await retryBusy(() =>
                request("appData.get", {
                    namespace: SETTINGS_NAMESPACE,
                    key: SETTINGS_KEY,
                })
            )
            version = current.version
            const currentSettings = current.found ? parsePersonalSettingsRecord(current.value)?.settings : undefined
            const merged = normalizePersonalSettings({ ...currentSettings, ...patch })
            replacePersonalSettings(store, merged)
            await writeSettings(merged, patch, false)
        }
    }

    async function sync(): Promise<void> {
        if (writeTimer) {
            globalThis.clearTimeout(writeTimer)
            writeTimer = null
        }
        const settings = store.settings.get()
        const patch = pendingPatch
        pendingPatch = {}
        if (!hasSettingsPatch(patch)) return
        const run = () => writeSettings(settings, patch, true)
        const result = writeQueue.then(run, run)
        writeQueue = result.catch(() => undefined)
        return result
    }

    return {
        store,
        sync,
        disconnect: () => {
            disposed = true
            if (writeTimer) {
                globalThis.clearTimeout(writeTimer)
                writeTimer = null
            }
        },
    }
}
